/**
 * viewport4D.js — Enterprise 4D Cardiac Phase Viewer
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — COMPLETE CARDIAC SUITE]:
 *  - start4DPlayback: setTimeout вместо setInterval (защита от гонки)
 *  - stop4DPlayback: clearTimeout для timeoutId
 *  - setCardiacPhase: ожидание завершения vol.load() перед loaded=true
 *  - preloadAllPhases: ожидание завершения vol.load()
 *  - set4DSpeed: сохранение текущего направления (bounce/forward)
 *  - detectCardiacPhases: дополнительные DICOM-теги
 *  - setCardiacPhase: проверка на пустой validIds
 *  - computeEF(): фракция выброса по объёмам систолы/диастолы
 *  - comparePhases(): сравнение двух фаз в соседних вьюпортах
 *  - overlayPhases(): наложение фаз с цветовым кодированием разницы
 *  - detectBestReconstructionPhase(): автовыбор фазы для 3D
 *  - getPhaseTimeline(): расширенный (с EF и рекомендованной фазой)
 *  - init4DViewer: автоматическое определение систолы/диастолы через imageIds
 *  - createTimelineUI(): встроенный UI для R-R интервала
 *  - autoInit4DFromVolume(): автоинициализация из DICOM-тегов при загрузке
 *
 * [ENTERPRISE FEATURES]
 *   ✓ Multi-phase navigation with auto-preload
 *   ✓ Systole/diastole auto-detection from DICOM tags
 *   ✓ Bounce playback (systole → diastole → systole)
 *   ✓ Phase comparison — side-by-side systole vs diastole
 *   ✓ Estimated EF (Ejection Fraction) — по разнице объёмов систолы/диастолы
 *   ✓ Optimal reconstruction phase detection
 *   ✓ Phase timeline UI для визуализации R-R интервала
 *   ✓ Speed control с сохранением текущей фазы и направления
 *   ✓ Strain vectors placeholder (разница систола→диастола)
 *
 * @module viewport4D
 */

import { getRenderingEngine, getViewportIds, cornerstone, ViewportType } from './viewportState.js';
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';
// [10.19.15] Разбор метаданных фаз, выбор КДО/КСО и сама ФВ переехали в
// core/cardiacPhases.js — там это проверяется на известном ответе без браузера.
// Здесь остаётся работа с Cornerstone: загрузка фаз, камера, показ.
import {
  фазыИзМетаданных, выбратьФазыДляФВ, фракцияВыброса, объёмКонтрастаВКадре, МЕТОД,
} from './cardiacPhases.js';
// [9.0.0] 4D-фазы создаются здесь, мимо loadVolume. Без записи в реестре
// восстановление после ухода в фон докладывало «нечем восстановить» —
// именно это и пришло в отчёте с устройства (случай C-нечем, томов: 1).
import { rememberVolume } from './volumeRegistry.js';

let _4dState = null;

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

/**
 * Дождаться полной загрузки volume.
 * @param {Object} vol — Cornerstone volume object
 * @param {number} [timeoutMs=30000]
 * @returns {Promise<void>}
 */
function _awaitVolumeLoad(vol, timeoutMs = 30000) {
  return new Promise((resolve, reject) => {
    if (vol.loadStatus?.loaded || vol.isLoaded?.()) return resolve();

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      try {
        if (vol.loadStatus?.loaded || vol.isLoaded?.()) {
          clearInterval(checkInterval);
          resolve();
        } else if (Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          reject(new Error('Volume load timeout'));
        }
      } catch (e) {
        clearInterval(checkInterval);
        reject(e);
      }
    }, 100);

    try { if (typeof vol.load === 'function') vol.load(); }
    catch (e) { clearInterval(checkInterval); reject(e); }
  });
}

function _fullVolumeId(volId) {
  return volId.startsWith('cornerstoneStreamingImageVolume:')
    ? volId : `cornerstoneStreamingImageVolume:${volId}`;
}

function _filterNonStackViewports(viewportIds) {
  const re = getRenderingEngine();
  if (!re) return [];
  return viewportIds.filter(id => {
    try { const vp = re.getViewport(id); return vp && vp.type !== ViewportType.STACK; }
    catch (e) { return false; }
  });
}

/** Получить скалярные данные объёма (первый попавшийся) */
function _getVolumeScalarData(volId) {
  try {
    const vol = cornerstone.cache.getVolume?.(volId) || cornerstone.cache.getVolume?.(_fullVolumeId(volId));
    if (!vol) return null;
    return vol.voxelManager?.getCompleteScalarDataArray?.()
      || vol.imageData?.getPointData?.()?.getScalars?.()?.getData?.()
      || vol.getScalarData?.()
      || null;
  } catch (e) { return null; }
}

/** Грубая оценка объёма ЛЖ по числу вокселей крови (HU 150–500 на контрастном КТ) */
function _estimateLVVolumeFromScalars(scalarData, spacing) {
  if (!scalarData?.length || !spacing) return null;
  let voxelCount = 0;
  const voxelVol = spacing[0] * spacing[1] * spacing[2];
  for (let i = 0; i < scalarData.length; i++) {
    const hu = scalarData[i];
    if (hu >= 150 && hu <= 500) voxelCount++; // контрастированная кровь
  }
  return +(voxelCount * voxelVol / 1000).toFixed(1); // мл
}

/** Поиск фазы по проценту (ближайшая) */
function _findPhaseByPercent(percent) {
  if (!_4dState) return null;
  let best = 0, bestDist = Infinity;
  for (let i = 0; i < _4dState.phases.length; i++) {
    const dist = Math.abs(_4dState.phases[i].phasePercent - percent);
    if (dist < bestDist) { bestDist = dist; best = i; }
  }
  return best;
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC API — ИНИЦИАЛИЗАЦИЯ
// ══════════════════════════════════════════════════════════════

/**
 * Initialize 4D viewer from multiple cardiac phases.
 * @param {Object[]} phases — [{ volumeId, imageIds, phasePercent, label, spacing }]
 * @param {string[]} viewportIds — target viewport IDs
 * @returns {Object|null}
 */
export function init4DViewer(phases, viewportIds) {
  if (!phases?.length) return null;

  stop4DPlayback();
  _4dState = null;

  _4dState = {
    phases: phases.map((p, i) => ({
      index: i,
      volumeId: p.volumeId,
      imageIds: p.imageIds,
      // [10.19.15] Процент НЕ выдумывается из номера кадра: раньше при отсутствии
      // метаданных фазе присваивался i/N·100, и она получала подпись «Систола»
      // просто по своему месту в списке. Нет данных — нет процента.
      phasePercent: Number.isFinite(Number(p.phasePercent)) ? Number(p.phasePercent) : null,
      label: p.label || (Number.isFinite(Number(p.phasePercent))
        ? `Фаза ${Math.round(Number(p.phasePercent))}%`
        : `Фаза ${i + 1} из ${phases.length}`),
      spacing: p.spacing || null,
      loaded: false,
    })),
    currentPhase: 0,
    viewportIds,
    timeoutId: null,
    playing: false,
    direction: 'forward',
    systoleIndex: null,
    diastoleIndex: null,
    bestReconPhase: null,
    ef: null,
    timelineUI: null,
  };

  // [10.19.15] Систола и диастола по проценту — ТОЛЬКО если процент известен.
  //
  // Прежний код сравнивал с окнами 25–45% и 65–85% что угодно, включая
  // миллисекунды и число кадров (см. detectCardiacPhases). Теперь неизвестный
  // процент — это null, и подписи по нему не выставляются. Для фракции выброса
  // это неважно: computeEF выбирает фазы по ИЗМЕРЕННЫМ объёмам, а не по подписи.
  for (const p of _4dState.phases) {
    const pct = p.phasePercent;
    if (pct === null || pct === undefined || !Number.isFinite(Number(pct))) continue;
    if (pct >= 25 && pct <= 45 && _4dState.systoleIndex === null) _4dState.systoleIndex = p.index;
    if (pct >= 65 && pct <= 85 && _4dState.diastoleIndex === null) _4dState.diastoleIndex = p.index;
  }
  _4dState.процентИзвестен = _4dState.phases.every((p) => Number.isFinite(Number(p.phasePercent)));

  // Автовыбор фазы для 3D реконструкции (наименьшее движение = 75% R-R)
  _4dState.bestReconPhase = _findPhaseByPercent(75);

  events.emit(EVENTS.PHASE_CHANGED, {
    phase: 0, totalPhases: phases.length, label: _4dState.phases[0].label,
  });

  log.debug(`[4D] Initialized: ${phases.length} phases, systole=#${_4dState.systoleIndex}, diastole=#${_4dState.diastoleIndex}, bestRecon=#${_4dState.bestReconPhase}`);
  return _4dState;
}

/**
 * Автоинициализация 4D из метаданных DICOM при загрузке volume.
 * Вызывается из loadVolume() если обнаружена многофазная серия.
 *
 * @param {Object} volume — загруженный Cornerstone volume
 * @param {Object} metadata — метаданные серии
 * @param {string[]} viewportIds
 * @returns {Object|null} _4dState или null
 */
export function autoInit4DFromVolume(volume, metadata, viewportIds) {
  try {
    const cardiacPhases = metadata?.cardiacPhases || metadata?.phases;
    if (!cardiacPhases?.length || cardiacPhases.length < 2) return null;

    // У нас уже есть загруженный volume — используем его imageIds как первую фазу
    const phases = cardiacPhases.map((cp, i) => ({
      volumeId: cp.volumeId || `${volume.volumeId}_phase${i}`,
      imageIds: cp.imageIds || (i === 0 ? volume.imageIds : []),
      phasePercent: cp.phasePercent ?? cp.nominalPercentage ?? Math.round((i / cardiacPhases.length) * 100),
      label: cp.label,
      spacing: volume.spacing,
    }));

    const state = init4DViewer(phases, viewportIds);
    if (state) {
      // Помечаем первую фазу как загруженную
      state.phases[0].loaded = true;
      log.info(`[4D] Auto-initialized from DICOM: ${phases.length} phases`);
    }
    return state;
  } catch (e) {
    log.warn('[4D] autoInit4DFromVolume failed:', e.message);
    return null;
  }
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC API — НАВИГАЦИЯ ПО ФАЗАМ
// ══════════════════════════════════════════════════════════════

export async function setCardiacPhase(phaseIndex) {
  try {
    if (!_4dState || phaseIndex < 0 || phaseIndex >= _4dState.phases.length) return false;
    const re = getRenderingEngine(); if (!re) return false;
    const phase = _4dState.phases[phaseIndex];
    _4dState.currentPhase = phaseIndex;
    const fullId = _fullVolumeId(phase.volumeId);

    try {
      let vol = cornerstone.cache.getVolume?.(fullId);
      if (!vol) {
        vol = await cornerstone.volumeLoader.createAndCacheVolume(fullId, { imageIds: phase.imageIds });
        await _awaitVolumeLoad(vol);
        phase.loaded = true;
        if (phase.spacing) phase.spacing = vol.spacing || phase.spacing;
      }
      const validIds = _filterNonStackViewports(_4dState.viewportIds);
      if (!validIds.length) { log.warn('[4D] Нет volume-вьюпортов'); return false; }
      // Запоминаем ТОЛЬКО показываемую фазу: реестр невелик, а заносить туда
      // все фазы разом значило бы вытеснить оттуда основной том исследования.
      try { rememberVolume(fullId, phase.imageIds, validIds, {}, 'main'); } catch (e) { /* */ }
      await cornerstone.setVolumesForViewports(re, [{ volumeId: fullId }], validIds, false);
      re.renderViewports(validIds);
      events.emit(EVENTS.PHASE_CHANGED, {
        phase: phaseIndex, totalPhases: _4dState.phases.length,
        phasePercent: phase.phasePercent, label: phase.label,
        isSystole: phaseIndex === _4dState.systoleIndex,
        isDiastole: phaseIndex === _4dState.diastoleIndex,
        isBestRecon: phaseIndex === _4dState.bestReconPhase,
      });
      // Обновляем timeline UI если есть
      if (_4dState.timelineUI) _updateTimelineUI();
      return true;
    } catch (e) { log.warn(`[4D] Phase ${phaseIndex}:`, e.message); return false; }
  } catch (e) { log.warn(`[viewport4D] setCardiacPhase failed:`, e.message); return false; }
}

export async function nextCardiacPhase() {
  if (!_4dState) return;
  await setCardiacPhase((_4dState.currentPhase + 1) % _4dState.phases.length);
}

export async function prevCardiacPhase() {
  if (!_4dState) return;
  await setCardiacPhase((_4dState.currentPhase - 1 + _4dState.phases.length) % _4dState.phases.length);
}

export async function jumpToSystole() {
  try {
    if (_4dState?.systoleIndex != null) { await setCardiacPhase(_4dState.systoleIndex); showToast('💓 Систола', 'info', 1200); }
    else showToast('⚠️ Систола не определена', 'warning', 1500);
  } catch (e) { log.warn(`[viewport4D] jumpToSystole failed:`, e.message); }
}

export async function jumpToDiastole() {
  try {
    if (_4dState?.diastoleIndex != null) { await setCardiacPhase(_4dState.diastoleIndex); showToast('💓 Диастола', 'info', 1200); }
    else showToast('⚠️ Диастола не определена', 'warning', 1500);
  } catch (e) { log.warn(`[viewport4D] jumpToDiastole failed:`, e.message); }
}

export async function jumpToBestReconPhase() {
  try {
    if (_4dState?.bestReconPhase != null) {
      await setCardiacPhase(_4dState.bestReconPhase);
      showToast(`📐 Оптимальная фаза для 3D (${_4dState.phases[_4dState.bestReconPhase].phasePercent}%)`, 'info', 2000);
    } else showToast('⚠️ Фаза не определена', 'warning', 1500);
  } catch (e) { log.warn(`[viewport4D] jumpToBestReconPhase failed:`, e.message); }
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC API — ФРАКЦИЯ ВЫБРОСА (EF)
// ══════════════════════════════════════════════════════════════

/**
 * Вычислить фракцию выброса по объёмам ЛЖ в систолу и диастолу.
 * Использует грубую оценку: воксели контрастированной крови (150–500 HU).
 *
 * @returns {Promise<{ef: number|null, edv: number|null, esv: number|null, method: string}|null>}
 */
export async function computeEF() {
  try {
    if (!_4dState) { showToast('Сначала откройте многофазную серию', 'warning', 2500); return null; }
    const фазы = _4dState.phases;
    if (!фазы || фазы.length < 2) {
      showToast('Для фракции выброса нужны минимум две фазы: по одной ФВ не существует', 'warning', 4000);
      return null;
    }

    // [10.19.15] Считаем объёмы по ВСЕМ фазам, а не только по двум «подписанным».
    //
    // Прежний код брал systoleIndex и diastoleIndex, выставленные по окнам
    // процентов (25–45% и 65–85%). При сдвинутых процентах — а они были сдвинуты,
    // см. detectCardiacPhases — эти индексы либо оставались null (и ФВ не
    // считалась вовсе), либо указывали не туда, и формула считала разность не тех
    // фаз. ФВ определена через ОБЪЁМЫ: КДО наибольший, КСО наименьший.
    const объёмы = [];
    for (const p of фазы) {
      if (!p.loaded) {
        try {
          const fullId = _fullVolumeId(p.volumeId);
          let vol = cornerstone.cache.getVolume?.(fullId);
          if (!vol) {
            vol = await cornerstone.volumeLoader.createAndCacheVolume(fullId, { imageIds: p.imageIds });
            await _awaitVolumeLoad(vol);
          }
          p.loaded = true;
        } catch (e) { log.warn('[4D] фаза не загрузилась:', e.message); }
      }
      const данные = _getVolumeScalarData(p.volumeId);
      const шаг = p.spacing || фазы[0].spacing || null;
      const о = объёмКонтрастаВКадре(данные, шаг);
      объёмы.push(о.мл);
    }

    const выбор = выбратьФазыДляФВ(объёмы, фазы.map((p) => ({ подпись: p.label, процент: p.phasePercent })));
    if (!выбор.надёжно) {
      showToast('Фракция выброса не посчитана: ' + выбор.причина, 'warning', 6000);
      return null;
    }
    for (const пред of выбор.предупреждения) showToast(пред, 'warning', 7000);

    const итог = фракцияВыброса({ кдо: выбор.кдо, ксо: выбор.ксо });
    if (!итог.надёжно) {
      showToast('Фракция выброса не посчитана: ' + итог.причина, 'warning', 6000);
      return null;
    }

    // ГЛАВНАЯ ОГОВОРКА, и она обязана быть на экране, а не только в коде.
    //
    // Объёмы здесь считаются по окну плотности 150–500 HU ПО ВСЕМУ КАДРУ: в него
    // попадают аорта, правый желудочок и предсердия. Это не объём ЛЖ, значит и
    // отношение — не фракция выброса левого желудочка. Прежняя версия печатала
    // «💓 ФВ: 58%» без единой оговорки; так число уходит в заключение.
    _4dState.ef = {
      ef: итог.фв, edv: итог.кдо, esv: итог.ксо, уо: итог.уо,
      method: 'отношение объёмов контраста в кадре (НЕ ФВ ЛЖ: нужна маска камеры)',
      надёжно: false,
      индексКДО: выбор.индексКДО, индексКСО: выбор.индексКСО,
      предупреждения: [...выбор.предупреждения, ...итог.предупреждения],
    };

    showToast(
      `Отношение объёмов: ${итог.фв}% (наибольший ${итог.кдо} мл — фаза «${фазы[выбор.индексКДО].label}», `
      + `наименьший ${итог.ксо} мл — «${фазы[выбор.индексКСО].label}»). `
      + 'ЭТО НЕ ФРАКЦИЯ ВЫБРОСА ЛЖ: объём считается по всему контрасту в кадре, включая аорту и правый желудочок. '
      + 'Для настоящей ФВ нужна маска левого желудочка.', 'warning', 12000);
    for (const пред of итог.предупреждения) showToast(пред, 'warning', 8000);

    events.emit('cardiac:ef:computed', _4dState.ef);
    return _4dState.ef;
  } catch (e) {
    log.warn('[4D] computeEF failed:', e.message);
    showToast('Фракция выброса не посчиталась: ' + e.message, 'error', 5000);
    return null;
  }
}

export function getEF() {
  return _4dState?.ef || null;
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC API — СРАВНЕНИЕ ФАЗ
// ══════════════════════════════════════════════════════════════

/**
 * Сравнение двух фаз: показывает phaseA в текущем вьюпорте, phaseB — в overlay.
 * Идеально для сравнения систола vs диастола.
 *
 * @param {number} phaseA — индекс первой фазы (основная)
 * @param {number} phaseB — индекс второй фазы (наложение)
 * @param {number} [opacity=0.5] — прозрачность наложения
 * @returns {Promise<boolean>}
 */
export async function comparePhases(phaseA, phaseB, opacity = 0.5) {
  try {
    if (!_4dState) return false;
    const re = getRenderingEngine(); if (!re) return false;

    const pA = _4dState.phases[phaseA];
    const pB = _4dState.phases[phaseB];
    if (!pA || !pB) return false;

    // Загружаем обе фазы
    for (const p of [pA, pB]) {
      if (!p.loaded) {
        const fullId = _fullVolumeId(p.volumeId);
        let vol = cornerstone.cache.getVolume?.(fullId);
        if (!vol) {
          vol = await cornerstone.volumeLoader.createAndCacheVolume(fullId, { imageIds: p.imageIds });
          await _awaitVolumeLoad(vol);
          p.loaded = true;
        }
      }
    }

    const validIds = _filterNonStackViewports(_4dState.viewportIds);
    if (!validIds.length) return false;

    // Основной том + наложение
    const fullIdA = _fullVolumeId(pA.volumeId);
    const fullIdB = _fullVolumeId(pB.volumeId);

    for (const vpId of validIds) {
      await cornerstone.setVolumesForViewports(re, [
        { volumeId: fullIdA },
        {
          volumeId: fullIdB,
          callback: ({ volumeActor }) => {
            try {
              const opFn = volumeActor?.getProperty?.()?.getScalarOpacity?.(0);
              if (opFn) { opFn.removeAllPoints(); opFn.addPoint(0, 0); opFn.addPoint(1, opacity); }
              // Зелёный оттенок для наложения (разница видна)
              const rgb = volumeActor?.getProperty?.()?.getRGBTransferFunction?.(0);
              if (rgb) {
                rgb.removeAllPoints();
                rgb.addRGBPoint(0, 0, 1, 0);    // чёрный → зелёный
                rgb.addRGBPoint(3000, 0, 1, 0);
              }
            } catch (e) { log.debug('[4D compare]', e.message); }
          },
        },
      ], [vpId], false);
    }

    re.renderViewports(validIds);
    _4dState.currentPhase = phaseA;

    showToast(`🔍 Сравнение: ${pA.label} против ${pB.label}`, 'info', 2000);
    return true;
  } catch (e) {
    log.warn('[4D] comparePhases failed:', e.message);
    return false;
  }
}

/**
 * Наложение систолы и диастолы с цветовым кодированием.
 * Упрощённый strain: зелёный = диастола, красный = систола.
 *
 * @param {number} [opacity=0.5]
 * @returns {Promise<boolean>}
 */
export async function overlayPhases(opacity = 0.5) {
  try {
    if (!_4dState) return false;
    const si = _4dState.systoleIndex;
    const di = _4dState.diastoleIndex;
    if (si == null || di == null) {
      showToast('⚠️ Систола/диастола не определены', 'warning', 2000);
      return false;
    }
    return await comparePhases(di, si, opacity);
  } catch (e) {
    log.warn('[4D] overlayPhases failed:', e.message);
    return false;
  }
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC API — PLAYBACK
// ══════════════════════════════════════════════════════════════

export function start4DPlayback(opts = {}) {
  if (!_4dState) return;
  stop4DPlayback();
  const fps = Math.max(1, Math.min(30, opts.fps || 5));
  const direction = opts.direction || 'forward';
  const loop = opts.loop !== false;
  const intervalMs = Math.round(1000 / fps);
  _4dState.playing = true;
  _4dState.direction = direction;
  let bounceDir = 1, running = false;

  const tick = async () => {
    if (!_4dState?.playing) return;
    if (running) { _4dState.timeoutId = setTimeout(tick, intervalMs); return; }
    running = true;
    try {
      let nextPhase;
      if (direction === 'bounce') {
        nextPhase = _4dState.currentPhase + bounceDir;
        if (nextPhase >= _4dState.phases.length - 1) { bounceDir = -1; nextPhase = _4dState.phases.length - 1; }
        else if (nextPhase <= 0) { bounceDir = 1; nextPhase = 0; }
      } else {
        nextPhase = _4dState.currentPhase + 1;
        if (nextPhase >= _4dState.phases.length) { if (loop) nextPhase = 0; else { stop4DPlayback(); return; } }
      }
      await setCardiacPhase(nextPhase);
    } catch (e) { log.warn('[4D] Playback tick:', e.message); }
    finally {
      running = false;
      if (_4dState?.playing) _4dState.timeoutId = setTimeout(tick, intervalMs);
    }
  };
  _4dState.timeoutId = setTimeout(tick, intervalMs);
  log.debug(`[4D] Playback: ${fps}fps ${direction}${loop ? ' loop' : ''}`);
}

export function stop4DPlayback() {
  if (!_4dState) return;
  _4dState.playing = false;
  if (_4dState.intervalId) { clearInterval(_4dState.intervalId); _4dState.intervalId = null; }
  if (_4dState.timeoutId) { clearTimeout(_4dState.timeoutId); _4dState.timeoutId = null; }
}

export function set4DSpeed(fps) {
  if (!_4dState?.playing) return;
  const direction = _4dState.direction || 'forward';
  stop4DPlayback();
  start4DPlayback({ fps: Math.max(1, Math.min(30, fps)), direction, loop: true });
  log.debug(`[4D] Speed: ${fps}fps (${direction})`);
}

// ══════════════════════════════════════════════════════════════
//  PUBLIC API — STATE & DETECTION
// ══════════════════════════════════════════════════════════════

export function get4DState() {
  if (!_4dState) return null;
  return {
    currentPhase: _4dState.currentPhase,
    totalPhases: _4dState.phases.length,
    phasePercent: _4dState.phases[_4dState.currentPhase]?.phasePercent,
    playing: _4dState.playing,
    direction: _4dState.direction,
    labels: _4dState.phases.map(p => p.label),
    systoleIndex: _4dState.systoleIndex,
    diastoleIndex: _4dState.diastoleIndex,
    bestReconPhase: _4dState.bestReconPhase,
    ef: _4dState.ef,
  };
}

export function getPhaseTimeline() {
  if (!_4dState) return [];
  return _4dState.phases.map(p => ({
    index: p.index,
    phasePercent: p.phasePercent,
    label: p.label,
    isCurrent: p.index === _4dState.currentPhase,
    isSystole: p.index === _4dState.systoleIndex,
    isDiastole: p.index === _4dState.diastoleIndex,
    isBestRecon: p.index === _4dState.bestReconPhase,
    loaded: p.loaded,
  }));
}

/**
 * [10.19.15] Фазы из метаданных — через core/cardiacPhases.js.
 *
 * ЧТО БЫЛО НЕ ТАК. Прежний порядок подстановки складывал несопоставимые
 * величины: процент фазы → время задержки в МИЛЛИСЕКУНДАХ → триггерное время в
 * МИЛЛИСЕКУНДАХ → ЧИСЛО КАДРОВ В ЦИКЛЕ. На серии с триггерными временами
 * 0…800 мс получались «фазы 0…800%», подписи «Систола» (окно 25–45%) и
 * «Диастола» (65–85%) не срабатывали вовсе, а систола с диастолой в
 * init4DViewer оставались неопределёнными — то есть ФВ было не из чего считать.
 * Число кадров в качестве процента — вообще не приближение, а мусор.
 *
 * Теперь: процент берётся из процентного тега; если его нет — пересчитывается из
 * времени задержки по интервалу R-R; если нет и этого — процента НЕТ, и подписи
 * «систола/диастола» не ставятся. Проверено 50 утверждениями в cardiac4d_test.
 */
export function detectCardiacPhases(seriesList) {
  if (!seriesList?.length) return [];
  const { фазы, метод, примечания } = фазыИзМетаданных(seriesList);
  if (примечания.length) log.info('[4D] фазы: ' + примечания.join('; '));
  return фазы.map((ф) => ({
    ...ф,
    // Прежние имена полей сохранены: на них смотрит остальной код и панели.
    phasePercent: ф.процент,
    label: ф.подпись,
    phaseIndex: ф.индекс,
    методФаз: метод,
  }));
}

export async function preloadAllPhases(onProgress) {
  if (!_4dState) return;
  let loaded = 0;
  const total = _4dState.phases.length;
  for (let i = 0; i < total; i++) {
    const p = _4dState.phases[i];
    if (p.loaded) { loaded++; if (onProgress) onProgress(loaded, total); continue; }
    const fullId = _fullVolumeId(p.volumeId);
    try {
      let vol = cornerstone.cache.getVolume?.(fullId);
      if (!vol) { vol = await cornerstone.volumeLoader.createAndCacheVolume(fullId, { imageIds: p.imageIds }); await _awaitVolumeLoad(vol); p.loaded = true; }
      else p.loaded = true;
    } catch (e) { log.warn(`[4D] Preload phase ${i}:`, e.message); }
    loaded++;
    if (onProgress) onProgress(loaded, total);
  }
  showToast(`✅ ${loaded}/${total} фаз загружено`, 'success', 2000);
}

/**
 * Определить оптимальную фазу для 3D-реконструкции коронаров.
 * Возвращает индекс фазы с минимальным движением.
 *
 * @returns {{phaseIndex: number, phasePercent: number, label: string}|null}
 */
export function detectBestReconstructionPhase() {
  if (!_4dState) return null;
  const idx = _4dState.bestReconPhase;
  if (idx == null) return null;
  const p = _4dState.phases[idx];
  return { phaseIndex: idx, phasePercent: p.phasePercent, label: p.label };
}

// ══════════════════════════════════════════════════════════════
//  TIMELINE UI (встроенный)
// ══════════════════════════════════════════════════════════════

/**
 * Создать/показать timeline UI в указанном контейнере.
 * @param {string} containerId — ID DOM-элемента
 */
export function createTimelineUI(containerId) {
  try {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Удаляем старый если есть
    if (_4dState?.timelineUI) {
      _4dState.timelineUI.remove();
      _4dState.timelineUI = null;
    }

    const timeline = document.createElement('div');
    timeline.className = 'cardiac-timeline';
    timeline.style.cssText = `
      display:flex; align-items:center; gap:2px; padding:4px 8px;
      background:rgba(0,0,0,0.6); border-radius:8px;
      font-family:-apple-system, sans-serif; font-size:10px; color:#aaa;
      user-select:none; -webkit-user-select:none;
    `;

    container.appendChild(timeline);
    _4dState.timelineUI = timeline;
    _updateTimelineUI();

    log.debug('[4D] Timeline UI created');
  } catch (e) {
    log.warn('[4D] createTimelineUI failed:', e.message);
  }
}

function _updateTimelineUI() {
  if (!_4dState?.timelineUI || !_4dState?.phases) return;
  const timeline = _4dState.timelineUI;
  timeline.innerHTML = '';

  // Кнопка Play/Pause
  const playBtn = document.createElement('button');
  playBtn.textContent = _4dState.playing ? '⏸' : '▶';
  playBtn.style.cssText = 'background:none; border:none; color:#fff; cursor:pointer; font-size:14px; padding:2px 4px;';
  playBtn.onclick = () => _4dState.playing ? stop4DPlayback() : start4DPlayback();
  timeline.appendChild(playBtn);

  // Фазы
  for (const p of _4dState.phases) {
    const dot = document.createElement('span');
    const isCurrent = p.index === _4dState.currentPhase;
    const isSystole = p.index === _4dState.systoleIndex;
    const isDiastole = p.index === _4dState.diastoleIndex;
    const isBestRecon = p.index === _4dState.bestReconPhase;

    dot.style.cssText = `
      display:inline-block; width:${isCurrent ? '18px' : '8px'}; height:${isCurrent ? '18px' : '8px'};
      border-radius:50%; cursor:pointer; transition:0.2s;
      background:${isSystole ? '#f44' : isDiastole ? '#4f8' : isBestRecon ? '#fa0' : isCurrent ? '#fff' : '#555'};
      border:${isBestRecon ? '2px solid #fa0' : '1px solid transparent'};
      opacity:${p.loaded ? '1' : '0.3'};
    `;
    dot.title = `${p.label}${isSystole ? ' (систола)' : ''}${isDiastole ? ' (диастола)' : ''}${isBestRecon ? ' ★3D' : ''}`;
    dot.onclick = () => setCardiacPhase(p.index);
    timeline.appendChild(dot);
  }

  // EF если вычислена
  if (_4dState.ef) {
    const efLabel = document.createElement('span');
    efLabel.textContent = `ФВ: ${_4dState.ef.ef}%`;
    efLabel.style.cssText = 'margin-left:8px; color:#0cf; font-weight:700; font-size:11px;';
    timeline.appendChild(efLabel);
  }
}

/**
 * Удалить timeline UI.
 */
export function destroyTimelineUI() {
  if (_4dState?.timelineUI) {
    _4dState.timelineUI.remove();
    _4dState.timelineUI = null;
  }
}